import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State } from '@stencil/core';
import { ICON_SIZES } from '../icon/models/icon.types';
import { DROPDOWN_ALIGN, DropdownAlign } from './models/dropdown-menu.types';

@Component({
  tag: 'wink-dropdown-menu',
  styleUrl: 'styles/dropdown-menu.default.scss',
  shadow: true,
})
export class DropdownMenu {
  @Element() host!: HTMLWinkDropdownMenuElement;

  @Prop() heading?: string = 'default-1';
  @Prop({ mutable: true }) isExpanded?: boolean = false;
  @Prop() dropdownTabIndex?: number = 0;
  @Prop() itemSelected?: string;
  @Prop() openOnHover? = false;

  @State() listPosition: DropdownAlign = DROPDOWN_ALIGN.topLeft;
  @State() focusableItem = NaN;
  @State() hasHoverSupport = false;

  @Event() winkDropdownMenuClicked!: EventEmitter<string>;

  componentDidLoad(): void {
    this.hasHoverSupport = window.matchMedia('(hover: hover)').matches;

    this.setPosition();
    document.addEventListener('click', this.handleOutsideClick);

    for (let i = 0; i < this.getDropdownItems().length; i++) {
      if ('innerText' in this.getDropdownItems()[i]) {
        const item = this.getDropdownItems()[i] as HTMLElement;
        if (item.innerText === this.itemSelected) {
          this.getDropdownItems()[i].classList.add('active');
        }
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleOutsideClick);
  }

  @Listen('mouseenter', {
    passive: true,
  })
  handleMouseEnter() {
    if ((!this.hasHoverSupport || !this.openOnHover) && !this.isExpanded) return;

    this.setPosition();
    this.isExpanded = true;
  }

  @Listen('mouseleave', {
    passive: true,
  })
  handleMouseLeave() {
    if ((!this.hasHoverSupport || !this.openOnHover) && this.isExpanded) return;

    this.isExpanded = false;
  }

  @Listen('keydown', {
    passive: true,
  })
  handleKeyDown(ev: KeyboardEvent) {
    switch (ev.key) {
      case ' ':
      case 'Enter': {
        const element = ev?.target as HTMLElement;
        if (element.tagName.toUpperCase() === 'WINK-PROGRESS-NAVIGATION-ITEMS') {
          element?.shadowRoot?.querySelector('a')?.click();
        } else {
          this.toggleDropdown(ev);
        }
        break;
      }
      case 'Tab': {
        if (ev.target === this.getDropdownItems()[this.getDropdownItems().length - 1]) {
          this.toggleDropdown(ev);
        }
        break;
      }
      case 'ArrowDown': {
        if (Number.isNaN(this.focusableItem) || this.focusableItem === this.getDropdownItems().length - 1) {
          this.focusableItem = 0;
        } else {
          this.focusableItem++;
        }
        this.focusOnItem(this.focusableItem);
        break;
      }
      case 'ArrowUp': {
        if (!this.focusableItem || this.focusableItem === -1) {
          this.focusableItem = this.getDropdownItems().length - 1;
        } else {
          this.focusableItem--;
        }
        this.focusOnItem(this.focusableItem);
        break;
      }
      case 'Escape': {
        this.isExpanded = false;
        this.host.shadowRoot?.querySelector('button')?.focus();
        break;
      }
      case 'Home': {
        this.focusableItem = 0;
        this.focusOnItem(this.focusableItem);
        break;
      }
      case 'End': {
        this.focusableItem = this.getDropdownItems().length - 1;
        this.focusOnItem(this.focusableItem);
        break;
      }
    }
  }

  private focusOnItem(focusItem: number) {
    const itemToFocus = this.getDropdownItems()[focusItem];
    if (itemToFocus instanceof HTMLElement) {
      return itemToFocus?.shadowRoot?.querySelector('a')?.focus();
    }
  }

  private readonly handleOutsideClick = (ev: MouseEvent) => {
    const path = ev.composedPath();

    if (!path.includes(this.host)) {
      this.isExpanded = false;
    }
  };

  private getDropdownItems() {
    if (this.host.querySelectorAll('wink-link').length === 0) {
      const slot = this.host.shadowRoot?.querySelector('.dropdown-menu--list slot');
      const assignedElements = slot ? (slot as HTMLSlotElement).assignedElements?.() : [];
      const nestedSlot = assignedElements[0];
      const nestedItems = nestedSlot ? (nestedSlot as HTMLSlotElement).assignedElements?.() : [];
      if (nestedItems && nestedItems.length) {
        return nestedItems.filter((item) => item.getAttribute('tabindex') !== '-1');
      }
    }
    return this.host.querySelectorAll('wink-link:not([is-disabled="true"])');
  }

  private readonly toggleDropdown = (_e: Event) => {
    this.setPosition();
    this.isExpanded = !this.isExpanded;
    this.winkDropdownMenuClicked.emit(this.isExpanded ? 'expanded' : 'collapsed');
  };

  private readonly setPosition = () => {
    const dropdownRect = this.host.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const spaceOnLeft = dropdownRect.left;
    const spaceOnRight = viewportWidth - dropdownRect.right;
    const spaceOnTop = dropdownRect.top;
    const spaceOnBottom = viewportHeight - dropdownRect.bottom;

    let positionX = Math.max(spaceOnLeft, spaceOnRight) === spaceOnRight ? 'left' : 'right';

    const centerX = dropdownRect.left + dropdownRect.width / 2;
    const viewportCenter = viewportWidth / 2;
    const centerTolerance = viewportWidth * 0.25;

    if (Math.abs(centerX - viewportCenter) < centerTolerance) {
      positionX = 'center';
    }

    const positionY = Math.max(spaceOnTop, spaceOnBottom) === spaceOnBottom ? 'top' : 'bottom';

    this.listPosition = `${positionY}-${positionX}` as DropdownAlign;
  };

  render() {
    return (
      <Host
        class={`dropdown-menu--align-list--${this.listPosition}`}
        is-expanded={this.isExpanded}
        exportparts="icon, icon-chevron-down-small">
        <button
          class="dropdown-menu--heading"
          tabindex={this.dropdownTabIndex}
          aria-haspopup="menu"
          aria-expanded={this.isExpanded ? 'true' : 'false'}
          onMouseDown={this.toggleDropdown}>
          <slot name="dropdown-menu--avatar" />
          {this.heading}
          <div class="dropdown-menu--chevron">
            <wink-icon size={ICON_SIZES.sm} icon="chevron-down-small"></wink-icon>
          </div>
        </button>
        <div class="dropdown-menu--list">
          <ul role="menu">
            <slot />
          </ul>
        </div>
      </Host>
    );
  }
}
